/** Catalogue figé des breadcrumbs KPI — copie admin (identique à src/lib/breadcrumbsCatalog.ts). */

export type BreadcrumbRole = 'candidate' | 'employer';
export type BreadcrumbPhase =
  | 'activation'
  | 'discovery'
  | 'social'
  | 'conversion'
  | 'offer'
  | 'pipeline'
  | 'return';

export interface BreadcrumbStep {
  id: string;
  n: number;
  phase: BreadcrumbPhase;
  label: string;
  fork?: boolean;
}

/** Comment la marche se comporte dans l’escalier admin. */
export type StairBand = 'pre' | 'stair' | 'fork' | 'milestone';

export function stepBand(step: BreadcrumbStep): StairBand {
  if (step.fork) return 'fork';
  const { id } = step;
  if (id === 'c.signup' || /^c\.onb_[1-8]$/.test(id)) return 'stair';
  if (id === 'e.signup' || id === 'e.email_sent' || id === 'e.email_verified') return 'stair';
  if (/^e\.offer_(sector|place|contract|requirements|advantages|visual|published)$/.test(id)) return 'stair';
  if (step.n < 6) return 'pre';
  return 'milestone';
}

export const CANDIDATE_STEPS: BreadcrumbStep[] = [
  { id: 'c.lang', n: 1, phase: 'activation', label: 'Langue' },
  { id: 'c.carousel', n: 2, phase: 'activation', label: 'Carrousel' },
  { id: 'c.welcome', n: 3, phase: 'activation', label: 'Welcome candidat' },
  { id: 'c.phone', n: 4, phase: 'activation', label: 'Téléphone' },
  { id: 'c.otp', n: 5, phase: 'activation', label: 'OTP' },
  { id: 'c.signup', n: 6, phase: 'activation', label: 'Compte créé' },
  { id: 'c.onb_1', n: 7, phase: 'activation', label: 'Onboarding 1 — Infos perso' },
  { id: 'c.onb_2', n: 8, phase: 'activation', label: 'Onboarding 2 — Langues' },
  { id: 'c.onb_3', n: 9, phase: 'activation', label: 'Onboarding 3 — Préférences' },
  { id: 'c.onb_4', n: 10, phase: 'activation', label: 'Onboarding 4 — Mobilité' },
  { id: 'c.onb_5', n: 11, phase: 'activation', label: 'Onboarding 5 — Accroche' },
  { id: 'c.onb_6', n: 12, phase: 'activation', label: 'Onboarding 6 — Expériences' },
  { id: 'c.onb_7', n: 13, phase: 'activation', label: 'Onboarding 7 — Diplômes' },
  { id: 'c.onb_8', n: 14, phase: 'activation', label: 'Onboarding 8 — Photo / complet' },
  { id: 'c.onb_skip', n: 15, phase: 'activation', label: 'Skip onboarding', fork: true },
  { id: 'c.feed_load', n: 16, phase: 'discovery', label: 'Premier fil' },
  { id: 'c.video_view', n: 17, phase: 'discovery', label: 'Première vue vidéo' },
  { id: 'c.mode_foryou', n: 18, phase: 'discovery', label: 'Mode Pour toi' },
  { id: 'c.mode_trending', n: 19, phase: 'discovery', label: 'Switch Tendances' },
  { id: 'c.filters', n: 20, phase: 'discovery', label: 'Filtres feed' },
  { id: 'c.open_offer', n: 21, phase: 'discovery', label: 'Fiche offre' },
  { id: 'c.open_employer', n: 22, phase: 'discovery', label: 'Fiche employeur' },
  { id: 'c.open_candidate', n: 23, phase: 'discovery', label: 'Fiche candidat' },
  { id: 'c.search', n: 24, phase: 'discovery', label: 'Recherche' },
  { id: 'c.search_result', n: 25, phase: 'discovery', label: 'Résultat de recherche' },
  { id: 'c.offers_tab', n: 26, phase: 'discovery', label: 'Tab Offres' },
  { id: 'c.like', n: 27, phase: 'social', label: 'Like' },
  { id: 'c.react', n: 28, phase: 'social', label: 'Réaction emoji' },
  { id: 'c.share', n: 29, phase: 'social', label: 'Share' },
  { id: 'c.follow', n: 30, phase: 'social', label: 'Follow' },
  { id: 'c.favorite_offer', n: 31, phase: 'social', label: 'Favori offre' },
  { id: 'c.apply_feed', n: 32, phase: 'conversion', label: 'Postuler (fil)' },
  { id: 'c.apply_detail', n: 33, phase: 'conversion', label: 'Postuler (fiche)' },
  { id: 'c.message', n: 34, phase: 'conversion', label: 'Premier message' },
  { id: 'c.inbox', n: 35, phase: 'return', label: 'Inbox' },
];

export const EMPLOYER_STEPS: BreadcrumbStep[] = [
  { id: 'e.lang', n: 1, phase: 'activation', label: 'Langue' },
  { id: 'e.carousel', n: 2, phase: 'activation', label: 'Carrousel' },
  { id: 'e.welcome', n: 3, phase: 'activation', label: 'Welcome recruteur' },
  { id: 'e.reg_you', n: 4, phase: 'activation', label: 'Register 1 — Vous' },
  { id: 'e.reg_siret', n: 5, phase: 'activation', label: 'Register 2 — Entreprise / SIRET' },
  { id: 'e.signup', n: 6, phase: 'activation', label: 'Compte créé' },
  { id: 'e.email_sent', n: 7, phase: 'activation', label: 'Code email envoyé' },
  { id: 'e.email_verified', n: 8, phase: 'activation', label: 'Email vérifié' },
  { id: 'e.feed_load', n: 9, phase: 'activation', label: 'Premier fil' },
  { id: 'e.video_view', n: 10, phase: 'activation', label: 'Première vue vidéo' },
  { id: 'e.mode_foryou', n: 11, phase: 'activation', label: 'Mode Pour toi' },
  { id: 'e.mode_trending', n: 12, phase: 'discovery', label: 'Switch Tendances' },
  { id: 'e.filters', n: 13, phase: 'discovery', label: 'Filtres feed' },
  { id: 'e.like', n: 14, phase: 'social', label: 'Like' },
  { id: 'e.react', n: 15, phase: 'social', label: 'Réaction emoji' },
  { id: 'e.share', n: 16, phase: 'social', label: 'Share' },
  { id: 'e.follow', n: 17, phase: 'social', label: 'Follow' },
  { id: 'e.search_global', n: 18, phase: 'discovery', label: 'Recherche globale' },
  { id: 'e.search_matching', n: 19, phase: 'discovery', label: 'Recherche candidats (matching)' },
  { id: 'e.offer_sector', n: 20, phase: 'offer', label: 'Wizard sector' },
  { id: 'e.offer_place', n: 21, phase: 'offer', label: 'Wizard place' },
  { id: 'e.offer_contract', n: 22, phase: 'offer', label: 'Wizard contract' },
  { id: 'e.offer_requirements', n: 23, phase: 'offer', label: 'Wizard requirements' },
  { id: 'e.offer_advantages', n: 24, phase: 'offer', label: 'Wizard advantages' },
  { id: 'e.offer_visual', n: 25, phase: 'offer', label: 'Wizard visual' },
  { id: 'e.offer_published', n: 26, phase: 'offer', label: 'Offre publiée' },
  { id: 'e.free_content', n: 27, phase: 'offer', label: 'Contenu libre' },
  { id: 'e.offers_list', n: 28, phase: 'pipeline', label: 'Liste Mes offres' },
  { id: 'e.applications_list', n: 29, phase: 'pipeline', label: 'Liste postulants' },
  { id: 'e.viewed_list', n: 30, phase: 'pipeline', label: 'Liste profils consultés' },
  { id: 'e.profile_view', n: 31, phase: 'pipeline', label: 'Voir fiche candidat' },
  { id: 'e.favorite_candidate', n: 32, phase: 'pipeline', label: 'Favori candidat' },
  { id: 'e.contact', n: 33, phase: 'pipeline', label: 'Contacter' },
  { id: 'e.message', n: 34, phase: 'pipeline', label: 'Premier message' },
  { id: 'e.inbox', n: 35, phase: 'return', label: 'Inbox' },
  { id: 'e.dashboard', n: 36, phase: 'return', label: 'Dashboard' },
];

export const PHASE_LABELS: Record<BreadcrumbPhase, string> = {
  activation: 'Activation',
  discovery: 'Discovery',
  social: 'Social',
  conversion: 'Conversion',
  offer: 'Annonce',
  pipeline: 'Pipeline',
  return: 'Retour',
};
